import { useNotificationContext } from '../../index'
import usePhotoesStore from '../../store/robotPhotoes'
import useRobot from 'hooks/useRobot'
import RobotOnMapImage from 'images/robot_on_map.png'
import { Feature } from 'ol'
import { Coordinate } from 'ol/coordinate'
import { LineString, Point } from 'ol/geom'
import VectorLayer from 'ol/layer/Vector'
import { fromLonLat } from 'ol/proj'
import VectorSource from 'ol/source/Vector'
import Icon from 'ol/style/Icon'
import Style from 'ol/style/Style'
import { useEffect, useRef, useState } from 'react'
import useMapStore from 'store/map'
import useTelemetryStore from 'store/robotTelemetry'
import useMissionStatusStore from 'store/robotMissionStatus'
import WebsocketManager, { SOCKET_MESSAGES } from 'utils/WebsocketManager'
import type { ControlConfirmDataDto } from 'api/asyncapi'
import { CONTROL_ERROR_SUCCESS, getControlErrorMessage } from 'utils/mqttErrorMessages'

interface Props {
  onPositionChange?: (position: Coordinate | null) => void
}

function SingleRobotLayer({ onPositionChange }: Props) {
  const map = useMapStore(s => s.map)
  const onPositionChangeRef = useRef(onPositionChange)
  onPositionChangeRef.current = onPositionChange

  const setTelemetry = useTelemetryStore(s => s.setTelemetry)
  const trajectory = useTelemetryStore(s => s.trajectory)
  const pushTrajectory = useTelemetryStore(s => s.pushTrajectory)
  const clearTrajectory = useTelemetryStore(s => s.clearTrajectory)
  const clearTelemetry = useTelemetryStore(s => s.clearTelemetry)

  const clearPhotoes = usePhotoesStore(s => s.clearPhotoes)
  const setFrontPhoto = usePhotoesStore(s => s.setFrontPhoto)
  const setRearPhoto = usePhotoesStore(s => s.setRearPhoto)

  const { data: robot } = useRobot()
  const robotSerialNumber = robot?.serialNumber
  const setMissionStatus = useMissionStatusStore(s => s.setMissionStatus)

  const [robotLayer, setRobotLayer] = useState<VectorLayer<VectorSource> | null>(null)
  const [trajectoryLayer, setTrajectoryLayer] = useState<VectorLayer<VectorSource> | null>(null)

  const { addNotification } = useNotificationContext()

  useEffect(() => {
    if (!map) return

    const layer = new VectorLayer({
      source: new VectorSource(),
      zIndex: 3,
    })

    map.addLayer(layer)
    setRobotLayer(layer)

    const trajectoryLayer = new VectorLayer({
      source: new VectorSource(),
      style: {
        'stroke-width': 3,
        'stroke-color': '#9747FF',
      },
      zIndex: 2,
    })
    map.addLayer(trajectoryLayer)
    setTrajectoryLayer(trajectoryLayer)

    return () => {
      map?.removeLayer(layer)
      map?.removeLayer(trajectoryLayer)
      clearTrajectory()
      clearTelemetry()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map])

  useEffect(() => {
    try {
      const feature = new Feature({
        geometry: new LineString(trajectory).transform('EPSG:4326', 'EPSG:3857'),
      })
      trajectoryLayer?.getSource()?.clear()
      trajectoryLayer?.getSource()?.addFeature(feature)
    } catch (error) {
      /* eslint-disable-next-line no-console */
      console.log('error')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trajectory])

  useEffect(() => {
    onPositionChangeRef.current?.(null)
    robotLayer?.getSource()?.clear()
    if (!robotSerialNumber || !robotLayer) return
    let websocketMessagesCount = 0
    let hasCenteredOnPosition = false

    const callbackTelemetry = (data: any) => {
      const longitude = data.pose?.position?.longitude
      const latitude = data.pose?.position?.latitude
      const position: [number, number] | null =
        typeof longitude === 'number' &&
        Number.isFinite(longitude) &&
        typeof latitude === 'number' &&
        Number.isFinite(latitude)
          ? [longitude, latitude]
          : null
      const orientation = data.orientation_degrees ?? 90

      if (position) {
        const coordinates = fromLonLat(position)
        const feature = new Feature({ geometry: new Point(coordinates) })
        feature.setStyle(
          new Style({
            image: new Icon({
              src: RobotOnMapImage,
              scale: 0.75,
              rotation: orientation * (Math.PI / 180),
            }),
          })
        )

        robotLayer.getSource()?.clear()
        robotLayer.getSource()?.addFeature(feature)
        onPositionChangeRef.current?.(coordinates)

        if (!hasCenteredOnPosition) {
          map?.getView().setCenter(coordinates)
          map?.getView().setZoom(18)
          hasCenteredOnPosition = true
        }
      }

      websocketMessagesCount += 1

      if (websocketMessagesCount % 3 === 0) {
        setTelemetry({
          timestamp: data.header.stamp.sec,
          batteryPower: data.battery_capacity,
          waterTankFilling: data.water_capacity,
          trashFullness: data.trash_capacity,
          locks: {
            body: data.body_lock,
            bodyIsLoading: data.body_lock_is_loading,
            front: data.front_lock,
            frontIsLoading: data.front_lock_is_loading,
            charge: data.charge_lock,
            chargeIsLoading: data.charge_lock_is_loading,
            trash: data.trash_lock,
            trashIsLoading: data.trash_lock_is_loading,
            watering: data.watering,
            wateringIsLoading: data.watering_is_loading,
            light: data.front_light,
            lightIsLoading: data.front_light_is_loading,
            dangerButton: data.danger_button,
            dangerButtonIsLoading: data.danger_button_is_loading,
          },
          cleaningEquipmentState: data.cleaning_equipment_state,
          cleaningEquipmentStateIsLoading: data.cleaning_equipment_state_is_loading,
          rtkMode: data.rtk_mode,
          statusCode: data.status_code,
          orientation: data.orientation_degrees,
          velocity: data.linear_velocity,
          missionStatus: data.mission_status,
          activeCommand: data.control_action,
          activeCommandIsLoading: data.robot_active_command_is_loading,
          activeMode: data.control_mode,
          activeModeIsLoading: data.robot_active_mode_is_loading,
          missionProgress: data.mission_progress,
        })

        if (position) pushTrajectory(position)
      }
    }

    const callbackControl = (data: ControlConfirmDataDto) => {
      if (data.error === CONTROL_ERROR_SUCCESS) {
        addNotification({ message: 'Изменение успешно', severity: 'success' })
      } else {
        addNotification({ message: getControlErrorMessage(data.error), severity: 'error' })
      }
    }

    const callbackPhotoes = (data: any) => {
      if (typeof data.photo === 'string') {
        if (data.camera === 'front') {
          setFrontPhoto(data.photo)
        } else {
          setRearPhoto(data.photo)
        }
      } else {
        console.error('Ошибка: поле photo не является строкой', data)
      }
    }

    const callbackMissionStatus = (data: any) => {
      setMissionStatus(data)
    }

    const robotWsPath = '/robot/' + robotSerialNumber
    WebsocketManager.init(robotWsPath)

    WebsocketManager.addEventListener(SOCKET_MESSAGES.MQTT_MESSAGES, callbackTelemetry, robotWsPath)
    WebsocketManager.addEventListener(SOCKET_MESSAGES.MQTT_CONTROL, callbackControl, robotWsPath)
    WebsocketManager.addEventListener(SOCKET_MESSAGES.ROBOT_PHOTOES, callbackPhotoes, robotWsPath)
    WebsocketManager.addEventListener(SOCKET_MESSAGES.MISSION_STATUS, callbackMissionStatus, robotWsPath)
    return () => {
      onPositionChangeRef.current?.(null)
      WebsocketManager.removeEventListener(SOCKET_MESSAGES.MQTT_MESSAGES, callbackTelemetry, robotWsPath)
      WebsocketManager.removeEventListener(SOCKET_MESSAGES.MQTT_CONTROL, callbackControl, robotWsPath)
      WebsocketManager.removeEventListener(SOCKET_MESSAGES.ROBOT_PHOTOES, callbackPhotoes, robotWsPath)
      WebsocketManager.removeEventListener(SOCKET_MESSAGES.MISSION_STATUS, callbackMissionStatus, robotWsPath)
      clearTelemetry()
      clearPhotoes()
      WebsocketManager.disconnect(robotWsPath)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [robotSerialNumber, robotLayer])

  return null
}

export default SingleRobotLayer
